import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { IProduct, ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import {
	Accordion,
	AccordionItem,
	AccordionItemPadding
} from '@inter-ikea-kompis/component-accordion';
import { Text } from '@inter-ikea-kompis/component-text';

import WoodDeclarationCard from '../wood-declaration-card/WoodDeclarationCard.js';
import WoodDeclarationStyle from './WoodDeclarationStyle.js';

const WOOD_DECLARATION_CODE = '00692';
/**
 * The wood declaration component displays the wood declaration for a product.
 *
 * @event woodDeclarationExpand Triggered when accordion is clicked.
 */

export default class WoodDeclaration extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Expands the accordion.
	@property({ type: Boolean })
	public woodDeclarationExpanded = false;

	// An object of type IProduct.
	@property({ type: Object, required: true })
	public product!: IProduct;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		if (
			!this.product?.content?.complianceInformation?.find(
				(compliance) => compliance.datasetCode === WOOD_DECLARATION_CODE
			)
		) {
			return null;
		}

		return html`
			<style>
				${WoodDeclarationStyle.getStyle()}
			</style>
			<div class="container">
				<kompis-accordion .theme="${this.theme}">
					<kompis-accordion-item
						@expand="${this.onAccordionExpand.bind(this)}"
						.label="${this.getHeader()}"
						.theme="${this.theme}"
						.expanded="${this.woodDeclarationExpanded}"
						.noHorizontalPadding="${true}"
					>
						<kompis-accordion-item-padding .theme="${this.theme}">
							<kompis-wood-declaration-card
								.theme="${this.theme}"
								.product="${this.product}"
							></kompis-wood-declaration-card>
						</kompis-accordion-item-padding>
					</kompis-accordion-item>
				</kompis-accordion>
			</div>
		`;
	}
	public override connectedCallback(): void {
		this.customElements.define('kompis-accordion', Accordion);
		this.customElements.define('kompis-accordion-item', AccordionItem);
		this.customElements.define('kompis-accordion-item-padding', AccordionItemPadding);
		this.customElements.define('kompis-text', Text);
		this.customElements.define('kompis-wood-declaration-card', WoodDeclarationCard);

		super.connectedCallback();
	}

	/**
	 * Dispatched event for when the accordion expands.
	 */
	private onAccordionExpand(): void {
		this.dispatchEvent(
			new CustomEvent('woodDeclarationExpand', {
				detail: { expand: !this.woodDeclarationExpanded },
				bubbles: true
			})
		);
	}

	/**
	 * Returns header for accordion from data.
	 */
	private getHeader(): string {
		const header = this.product.content?.complianceInformation?.find(
			(compliance) => compliance.datasetCode === WOOD_DECLARATION_CODE
		)?.headingText;

		return header || '';
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-wood-declaration': WoodDeclaration;
	}
}
